<template>
  <div class="content-wrapper">
    <div class="content-body">
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <b-alert
        v-else-if="error"
        show
        variant="danger"
      >
        <div class="alert-body">
          {{ error }}
        </div>
      </b-alert>

      <template v-else-if="projet">
        <!-- En-tête projet -->
        <b-card>
          <div class="d-flex flex-wrap align-items-start justify-content-between">
            <div>
              <div class="d-flex align-items-center mb-50">
                <feather-icon
                  icon="LayersIcon"
                  size="20"
                  class="mr-50 text-primary"
                />
                <h3 class="mb-0">
                  {{ projet.nom }}
                </h3>
              </div>
              <p
                v-if="projet.description"
                class="text-muted mb-0"
              >
                {{ projet.description }}
              </p>
            </div>
            <div class="d-flex flex-wrap">
              <b-button
                variant="primary"
                class="mr-50 mb-50"
                :disabled="loadingRecap"
                @click="downloadRecapPdf"
              >
                <b-spinner
                  v-if="loadingRecap"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="FileTextIcon"
                  size="14"
                  class="mr-50"
                />
                {{ loadingRecap ? 'Génération…' : 'PDF récapitulatif' }}
              </b-button>
              <b-button
                variant="outline-primary"
                class="mr-50 mb-50"
                @click="voirChantiers"
              >
                <feather-icon
                  icon="ListIcon"
                  size="14"
                  class="mr-50"
                />
                Voir les chantiers
              </b-button>
              <b-button
                v-if="$can('CHANTIER:update')"
                variant="outline-secondary"
                class="mb-50"
                @click="openEdit"
              >
                <feather-icon
                  icon="Edit2Icon"
                  size="14"
                  class="mr-50"
                />
                Modifier
              </b-button>
            </div>
          </div>
        </b-card>

        <!-- Chantiers rattachés -->
        <b-card>
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h5 class="mb-0">
              Chantiers rattachés
              <b-badge variant="light-primary">
                {{ projet.chantiers.length }}
              </b-badge>
            </h5>
            <b-button
              v-if="$can('CHANTIER:update')"
              size="sm"
              variant="primary"
              @click="openManage"
            >
              <feather-icon
                icon="SettingsIcon"
                size="14"
                class="mr-50"
              />
              Gérer les chantiers
            </b-button>
          </div>

          <div
            v-if="!projet.chantiers.length"
            class="text-muted"
          >
            Aucun chantier rattaché à ce projet.
          </div>
          <b-list-group v-else>
            <b-list-group-item
              v-for="c in projet.chantiers"
              :key="c.id"
              class="d-flex align-items-center justify-content-between"
              :to="{ name: 'chantier-detail', params: { id: c.id } }"
            >
              <span>{{ c.nom }}</span>
              <b-badge :style="statutStyle(c.statut)">
                {{ statutLabel(c.statut) }}
              </b-badge>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </template>

      <chantier-projet-form-modal
        ref="formModal"
        @saved="onSaved"
      />

      <!-- Modale de gestion des chantiers rattachés -->
      <b-modal
        id="projet-manage-chantiers-modal"
        title="Gérer les chantiers du projet"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        cancel-variant="outline-secondary"
        :ok-disabled="savingChantiers"
        centered
        size="lg"
        @ok.prevent="saveChantiers"
      >
        <b-input-group class="mb-1">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="manageSearch"
            placeholder="Filtrer les chantiers…"
          />
        </b-input-group>
        <div
          v-if="loadingChantiers"
          class="text-center py-2"
        >
          <b-spinner
            small
            variant="primary"
          />
        </div>
        <div
          v-else
          class="projet-manage-list"
        >
          <b-form-checkbox
            v-for="c in filteredAllChantiers"
            :key="c.id"
            v-model="selectedChantierIds"
            :value="c.id"
            class="mb-50"
          >
            {{ c.nom }}
            <b-badge
              v-if="c.projetId && c.projetId !== projetId"
              variant="light-warning"
              class="ml-50"
            >
              déjà dans un autre projet
            </b-badge>
          </b-form-checkbox>
          <div
            v-if="!filteredAllChantiers.length"
            class="text-muted"
          >
            Aucun chantier trouvé.
          </div>
        </div>
        <p
          v-if="manageError"
          class="text-danger mb-0 mt-1"
        >
          {{ manageError }}
        </p>
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner, BAlert,
  BListGroup, BListGroupItem, BModal,
  BInputGroup, BInputGroupPrepend, BFormInput, BFormCheckbox,
} from 'bootstrap-vue'
import ChantierProjetFormModal from '@/components/chantierProjets/ChantierProjetFormModal.vue'

export default {
  name: 'ChantierProjetDetailView',
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BAlert,
    BListGroup,
    BListGroupItem,
    BModal,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BFormCheckbox,
    ChantierProjetFormModal,
  },
  data() {
    return {
      projet: null,
      statuts: [],
      loading: true,
      error: null,
      loadingRecap: false,
      // Gestion des chantiers
      allChantiers: [],
      selectedChantierIds: [],
      manageSearch: '',
      loadingChantiers: false,
      savingChantiers: false,
      manageError: null,
    }
  },
  computed: {
    projetId() { return this.$route.params.id },
    filteredAllChantiers() {
      const q = (this.manageSearch || '').trim().toLowerCase()
      if (!q) return this.allChantiers
      return this.allChantiers.filter(c => (c.nom || '').toLowerCase().includes(q))
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const [projet, statuts] = await Promise.all([
          this.$http.myressif.chantierProjets.getById(this.projetId),
          this.$http.myressif.chantiers.getStatuts(),
        ])
        this.projet = projet
        this.statuts = statuts || []
      } catch (e) {
        this.projet = null
        this.error = 'Une erreur est survenue lors du chargement du projet.'
      } finally {
        this.loading = false
      }
    },
    voirChantiers() {
      this.$router.push({ name: 'chantiers-list', query: { projetId: this.projetId } })
    },
    async downloadRecapPdf() {
      this.loadingRecap = true
      try {
        const response = await this.$http.myressif.chantierProjets.downloadRecapPdf(this.projetId)
        const blob = new Blob([response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const slug = ((this.projet && this.projet.nom) || 'projet')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `recapitulatif-projet-${slug || 'projet'}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$bvModal.msgBoxOk('Impossible de générer le PDF récapitulatif.', {
          title: 'Erreur', okVariant: 'danger', centered: true,
        })
      } finally {
        this.loadingRecap = false
      }
    },
    openEdit() {
      this.$refs.formModal.open(this.projet)
    },
    onSaved() {
      this.load()
    },
    async openManage() {
      this.manageError = null
      this.manageSearch = ''
      this.selectedChantierIds = this.projet.chantiers.map(c => c.id)
      this.$bvModal.show('projet-manage-chantiers-modal')
      this.loadingChantiers = true
      try {
        // Charge un large lot de chantiers pour la sélection (pas de pagination dans la modale).
        const response = await this.$http.myressif.chantiers.search({ pageNumber: 1, pageSize: 500 })
        this.allChantiers = (response && response.items) || []
      } catch (e) {
        this.allChantiers = []
        this.manageError = 'Impossible de charger la liste des chantiers.'
      } finally {
        this.loadingChantiers = false
      }
    },
    async saveChantiers() {
      if (this.savingChantiers) return
      this.savingChantiers = true
      this.manageError = null
      try {
        await this.$http.myressif.chantierProjets.setChantiers(this.projetId, this.selectedChantierIds)
        this.$bvModal.hide('projet-manage-chantiers-modal')
        await this.load()
      } catch (e) {
        this.manageError = "L'enregistrement des chantiers a échoué."
      } finally {
        this.savingChantiers = false
      }
    },
    statut(id) {
      return this.statuts.find(s => s.id === id) || null
    },
    statutLabel(id) {
      const s = this.statut(id)
      return s ? s.valeur : '—'
    },
    statutStyle(id) {
      const s = this.statut(id)
      if (!s || !s.couleur) return {}
      return { backgroundColor: s.couleur, color: '#fff' }
    },
  },
}
</script>

<style scoped>
.projet-manage-list {
  max-height: 320px;
  overflow-y: auto;
}
</style>
